import { createContext, createElement, useContext, useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/lib/auth/auth-context"

export interface StreakState {
  current_streak: number
  longest_streak: number
  last_log_date: string | null
  freezes_available: number
  user_timezone: string
  no_spend_dates: string[]
}

const DEFAULT_STATE: StreakState = {
  current_streak: 0,
  longest_streak: 0,
  last_log_date: null,
  freezes_available: 0,
  user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  no_spend_dates: [],
}

/** Cheap-ish deep-equality for the streak payload. The only field that can
 * legitimately churn shape is `no_spend_dates` (string[]). Everything else is
 * a primitive. We compare arrays element-by-element so a refetch that returns
 * identical data leaves all `state.*` references stable, preventing downstream
 * components (StreakChip, StreakCalendar) from re-rendering on every refresh. */
function streakStateEqual(a: StreakState, b: StreakState): boolean {
  if (
    a.current_streak !== b.current_streak ||
    a.longest_streak !== b.longest_streak ||
    a.last_log_date !== b.last_log_date ||
    a.freezes_available !== b.freezes_available ||
    a.user_timezone !== b.user_timezone
  ) return false
  if (a.no_spend_dates === b.no_spend_dates) return true
  if (a.no_spend_dates.length !== b.no_spend_dates.length) return false
  for (let i = 0; i < a.no_spend_dates.length; i++) {
    if (a.no_spend_dates[i] !== b.no_spend_dates[i]) return false
  }
  return true
}

interface StreakContextValue extends StreakState {
  loading: boolean
  refresh: () => Promise<void>
}

// One shared streak fetch for the whole app. Before this, every component that
// called useStreakState() (StreakChip, BottomNav, Growth, Wardrobe, the mood/
// nudge/badge hooks, HomePage, ...) fired its own identical `streak_state`
// query on mount — ~11 duplicate round-trips per dashboard load. Worse, each
// kept an isolated copy of the state, so calling refresh() in one place (e.g.
// after "No-spend today") never updated the chip elsewhere. The provider makes
// the data single-source: one fetch, one refresh, every consumer in sync.
const StreakContext = createContext<StreakContextValue | null>(null)

export function StreakProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id
  const [state, setState] = useState<StreakState>(DEFAULT_STATE)
  const [loading, setLoading] = useState(true)
  // Stash latest state in a ref so `refresh` stays referentially stable across
  // renders — otherwise every parent tick allocates a new callback identity
  // and downstream effects depending on it run unnecessarily.
  const stateRef = useRef(state)
  stateRef.current = state

  const refresh = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    const { data, error } = await supabase
      .from("streak_state")
      .select("current_streak, longest_streak, last_log_date, freezes_available, user_timezone, no_spend_dates")
      .eq("user_id", userId)
      .maybeSingle()
    if (error) {
      console.error("[streak] fetch failed:", error)
    } else if (data) {
      const next: StreakState = { ...DEFAULT_STATE, ...data }
      // Skip the state update entirely when the payload matches the current
      // snapshot — preserves `no_spend_dates` array identity for memo deps.
      if (!streakStateEqual(stateRef.current, next)) {
        setState(next)
      }
    }
    setLoading(false)
  }, [userId])

  useEffect(() => { refresh() }, [refresh])

  const value = useMemo<StreakContextValue>(
    () => ({ ...state, loading, refresh }),
    [state, loading, refresh],
  )

  return createElement(StreakContext.Provider, { value }, children)
}

export function useStreakState(): StreakContextValue {
  const ctx = useContext(StreakContext)
  // Fallback keeps any consumer rendered outside the provider (stray dev/test
  // renders) from crashing. In the app shell the provider is always mounted.
  if (!ctx) return { ...DEFAULT_STATE, loading: false, refresh: async () => {} }
  return ctx
}

/** Days between two YYYY-MM-DD strings (laterDate - earlierDate). */
export function daysBetween(earlierDate: string, laterDate: string): number {
  const a = new Date(earlierDate + "T00:00:00")
  const b = new Date(laterDate + "T00:00:00")
  return Math.round((b.getTime() - a.getTime()) / 86400000)
}

/** YYYY-MM-DD in user's local TZ. */
export function todayLocal(): string {
  return new Date().toLocaleDateString("en-CA")
}
